import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft, BookmarkCheck, CheckCircle2, NotebookPen, Trash2, Save, Pencil, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  fetchTerm, reviewTerm, createNote, updateNote, deleteNote, formatApiError,
} from "@/lib/inmindApi";
import CategoryBadge from "@/components/CategoryBadge";
import ThinkPanel from "@/components/ThinkPanel";
import ShareButton from "@/components/ShareCard";

export default function TermDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [term, setTerm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState(null); // {id, content}

  const load = async () => {
    setLoading(true);
    try {
      const t = await fetchTerm(id);
      setTerm(t);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load term");
    } finally {
      setLoading(false);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const mark = async () => {
    setBusy(true);
    try {
      const next = term.status === "reviewed" ? "new" : "reviewed";
      const t = await reviewTerm(id, next);
      setTerm((cur) => ({ ...cur, ...t }));
      toast.success(next === "reviewed" ? "Marked reviewed" : "Marked new");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  const addNote = async () => {
    if (!draft.trim()) return;
    setBusy(true);
    try {
      const n = await createNote(id, draft);
      setTerm((cur) => ({ ...cur, notes: [n, ...(cur.notes || [])] }));
      setDraft("");
      toast.success("Note added");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't add note");
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      const updated = await updateNote(editing.id, editing.content);
      setTerm((cur) => ({
        ...cur,
        notes: (cur.notes || []).map((n) => (n.id === updated.id ? updated : n)),
      }));
      setEditing(null);
      toast.success("Saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save");
    } finally {
      setBusy(false);
    }
  };

  const removeNote = async (nid) => {
    if (!confirm("Delete this note?")) return;
    try {
      await deleteNote(nid);
      setTerm((cur) => ({ ...cur, notes: (cur.notes || []).filter((n) => n.id !== nid) }));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't delete");
    }
  };

  if (loading || !term) {
    return (
      <div className="flex items-center justify-center py-24 text-[var(--inmind-muted)]">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="term-detail">
      <button
        onClick={() => navigate(-1)}
        data-testid="back-btn"
        className="inline-flex items-center gap-1.5 text-sm text-[var(--inmind-text-secondary)] hover:text-[var(--inmind-text)] transition-colors"
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={1.8} /> Back
      </button>

      <article className="bg-white rounded-3xl border border-black/5 shadow-[0_10px_32px_rgba(0,0,0,0.05)] p-6 sm:p-8">
        <div className="flex items-center justify-between gap-3 mb-4">
          <CategoryBadge category={term.category} />
          {term.status === "reviewed" && (
            <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase font-semibold text-[var(--inmind-primary)]">
              <BookmarkCheck className="w-3 h-3" strokeWidth={2} /> Reviewed
            </span>
          )}
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl text-[var(--inmind-text)] leading-tight" data-testid="detail-term">
          {term.term}
        </h1>
        <p className="mt-4 text-[var(--inmind-text)] leading-relaxed">{term.meaning}</p>
        <div className="mt-6 pt-5 border-t border-black/5">
          <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--inmind-muted)] mb-1.5">
            Did you know
          </div>
          <p className="text-sm text-[var(--inmind-text-secondary)] leading-relaxed">{term.fact}</p>
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-2">
          <Button
            onClick={mark}
            disabled={busy}
            data-testid="detail-mark"
            className="rounded-full h-10 px-4 bg-[var(--inmind-primary)] text-white hover:bg-[var(--inmind-primary-hover)] transition-colors gap-1.5"
          >
            <CheckCircle2 className="w-4 h-4" strokeWidth={1.8} />
            {term.status === "reviewed" ? "Mark as new" : "Mark as reviewed"}
          </Button>
          <ShareButton term={term} />
          <Link
            to="/library"
            className="ml-auto text-sm font-medium text-[var(--inmind-text-secondary)] hover:text-[var(--inmind-primary)] transition-colors"
          >
            Back to library
          </Link>
        </div>
      </article>

      {/* Notes */}
      <Card className="rounded-2xl border border-black/5 bg-white shadow-[0_4px_18px_rgba(0,0,0,0.03)]">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <NotebookPen className="w-4 h-4 text-[var(--inmind-primary)]" strokeWidth={1.7} />
            <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--inmind-muted)]">
              Your notes on this term
            </div>
          </div>
          <div className="space-y-2">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Capture a quick reflection, example, or question…"
              data-testid="note-draft"
              className="min-h-[90px] rounded-xl text-sm leading-relaxed"
            />
            <Button
              onClick={addNote}
              disabled={busy || !draft.trim()}
              data-testid="note-add"
              className="rounded-xl bg-[var(--inmind-primary)] text-white hover:bg-[var(--inmind-primary-hover)] transition-colors gap-1.5"
            >
              <Plus className="w-4 h-4" strokeWidth={1.8} /> Add note
            </Button>
          </div>
          {term.notes && term.notes.length > 0 && (
            <ul className="mt-5 space-y-3" data-testid="notes-list">
              {term.notes.map((n) => (
                <li key={n.id} className="rounded-xl bg-[var(--inmind-card-subtle)] p-3 group" data-testid={`note-${n.id}`}>
                  {editing?.id === n.id ? (
                    <div className="space-y-2">
                      <Textarea
                        value={editing.content}
                        onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                        className="rounded-lg text-sm"
                        data-testid={`note-edit-${n.id}`}
                      />
                      <div className="flex gap-2">
                        <Button onClick={saveEdit} data-testid={`note-save-${n.id}`} className="rounded-lg h-8 px-3 bg-[var(--inmind-primary)] text-white text-xs gap-1">
                          <Save className="w-3 h-3" strokeWidth={2} /> Save
                        </Button>
                        <Button onClick={() => setEditing(null)} variant="ghost" className="rounded-lg h-8 px-3 text-xs">
                          <X className="w-3 h-3 mr-1" strokeWidth={2} /> Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm text-[var(--inmind-text)] whitespace-pre-wrap leading-relaxed">{n.content}</p>
                      <div className="flex items-center justify-between mt-2">
                        <span className="text-[10px] tracking-[0.18em] uppercase text-[var(--inmind-muted)]">
                          {new Date(n.updated_at || n.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                        </span>
                        <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => setEditing({ id: n.id, content: n.content })}
                            data-testid={`note-edit-btn-${n.id}`}
                            aria-label="Edit"
                            className="p-1 rounded text-[var(--inmind-muted)] hover:text-[var(--inmind-primary)]"
                          >
                            <Pencil className="w-3 h-3" strokeWidth={1.8} />
                          </button>
                          <button
                            onClick={() => removeNote(n.id)}
                            data-testid={`note-delete-${n.id}`}
                            aria-label="Delete"
                            className="p-1 rounded text-[var(--inmind-muted)] hover:text-red-500"
                          >
                            <Trash2 className="w-3 h-3" strokeWidth={1.8} />
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      <div className="lg:h-[520px]">
        <ThinkPanel termId={term.id} termName={term.term} />
      </div>
    </div>
  );
}
